import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Select, Input, Button, Space, Typography, Tag, Avatar, Tooltip, Card, Progress, Table, Checkbox, notification, Badge, Row, Col } from 'antd';
import { ReloadOutlined, UserOutlined, PlusOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import { PRIORITY_COLORS } from './TasksPage';

const COLUMNS = [
  { key: 'OPEN', title: 'To do', color: '#1677ff' },
  { key: 'WORKING', title: 'In progress', color: '#d48806' },
  { key: 'COMPLETED', title: 'Done (last 14 days)', color: '#389e0d' },
];
const initials = (name) => (name || '?').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

/** One task card on the board. Draggable between the status columns. */
function TaskCard({ task, today, onOpen }) {
  const overdue = task.status !== 'COMPLETED' && task.exp_end_date && task.exp_end_date < today;
  const soon = !overdue && task.status !== 'COMPLETED' && task.exp_end_date && task.exp_end_date <= moment(today).add(3, 'days').format('YYYY-MM-DD');
  const items = task.TaskChecklistItems || [];
  const done = items.filter((i) => i.is_done).length;
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/task', String(task.id))}
      onClick={() => onOpen(task)}
      style={{ background: '#fff', border: `1px solid ${overdue ? '#ffa39e' : '#e5e7eb'}`, borderLeft: `4px solid ${task.priority === 'HIGH' ? '#cf1322' : task.priority === 'LOW' ? '#d9d9d9' : '#1677ff'}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8, cursor: 'grab', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
    >
      <div style={{ fontWeight: 500, lineHeight: 1.3 }}>{task.subject}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
        {task.Project?.Client ? task.Project.Client.client_name : <em>Internal</em>}
        {task.Project ? ` - ${task.Project.name}` : ''}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 12 }}>
        <Space size={6}>
          {task.exp_end_date && (
            <span style={{ color: overdue ? '#cf1322' : soon ? '#d46b08' : '#6b7280', fontWeight: overdue ? 600 : 400 }}>
              <ClockCircleOutlined /> {moment(task.exp_end_date).format('DD MMM')}
            </span>
          )}
          {items.length > 0 && <span style={{ color: done === items.length ? '#389e0d' : '#6b7280' }}>{done}/{items.length}</span>}
          {task.priority === 'HIGH' && <Tag color={PRIORITY_COLORS.HIGH} style={{ marginRight: 0, lineHeight: '16px', fontSize: 10 }}>HIGH</Tag>}
        </Space>
        <Tooltip title={task.AssignedUser ? task.AssignedUser.name : 'Unassigned'}>
          <Avatar size={22} style={{ background: task.AssignedUser ? '#5b6cff' : '#d9d9d9', fontSize: 10 }}>{task.AssignedUser ? initials(task.AssignedUser.name) : <UserOutlined />}</Avatar>
        </Tooltip>
      </div>
    </div>
  );
}

/** Workbook (xlsx "Workbook", MENU "Workbook -> Main Dashboard"): the firm's work board over projects, tasks and deadlines. */
export default function WorkbookPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  // ?client=<id> (from a client's "Workbook status" tab) opens the board filtered to that client
  const linkedClient = params.get('client') ? Number(params.get('client')) : null;
  const [filters, setFilters] = React.useState({ mine: !isManager && !linkedClient, assigned_user_id: null, client_id: linkedClient, q: '' });
  const [board, setBoard] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(null);

  React.useEffect(() => {
    if (isSystemAdmin) {
      request.get('/companies').then((r) => r.json()).then((r) => {
        if (r.status) {
          setCompanies(r.data);
          setCompanyId((c) => c || (r.data.length ? r.data[0].id : null));
        }
      });
    }
  }, [isSystemAdmin, setCompanyId]);
  React.useEffect(() => {
    if (!companyId) return;
    request.get('/clients', { company_id: companyId }).then((r) => r.json()).then((r) => r.status && setClients(r.data));
  }, [companyId]);

  const load = React.useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    const q = { company_id: companyId };
    if (filters.mine) q.mine = '1';
    else if (filters.assigned_user_id) q.assigned_user_id = filters.assigned_user_id;
    if (filters.client_id) q.client_id = filters.client_id;
    if (filters.q) q.q = filters.q;
    request.get('/workbook/board', q).then((r) => r.json()).then((r) => (r.status ? setBoard(r.data) : notification.error({ message: r.message }))).finally(() => setLoading(false));
  }, [companyId, filters]);
  React.useEffect(load, [load]);

  const moveTask = (taskId, status) => {
    const task = board.tasks.find((t) => t.id === taskId);
    if (!task || task.status === status) return;
    request
      .post(`/tasks/${taskId}/status`, { status })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: `${task.subject} - ${status === 'OPEN' ? 'to do' : status === 'WORKING' ? 'in progress' : 'done'}` });
        load();
      });
  };
  const assignTask = (taskId, userId) => {
    request
      .put(`/workbook/tasks/${taskId}/assign`, { assigned_user_id: userId })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: `${r.data.subject} assigned to ${r.data.AssignedUser ? r.data.AssignedUser.name : 'nobody'}` });
        load();
      });
  };
  const onDrop = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = Number(e.dataTransfer.getData('text/task'));
    if (id) moveTask(id, status);
  };
  const onDropStaff = (e, userId) => {
    e.preventDefault();
    setDragOver(null);
    const id = Number(e.dataTransfer.getData('text/task'));
    if (id) assignTask(id, userId);
  };
  const openTask = (task) => navigate(`/tasks/${task.id}`);

  const staffOptions = board ? board.staff.map((s) => ({ value: s.id, label: s.name })) : [];
  const c = board ? board.counts : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Workbook
          </Typography.Title>
          <Typography.Text type="secondary">The work board: drag a card between columns to change its status, or onto a colleague to reassign it. Cards open the task.</Typography.Text>
        </div>
        <Space wrap>
          {isSystemAdmin && <Select style={{ width: 170 }} value={companyId} onChange={setCompanyId} options={companies.map((x) => ({ value: x.id, label: x.name }))} />}
          <Checkbox checked={filters.mine} onChange={(e) => setFilters((f) => ({ ...f, mine: e.target.checked, assigned_user_id: null }))}>My tasks</Checkbox>
          {!filters.mine && isManager && <Select allowClear showSearch optionFilterProp="label" style={{ width: 170 }} placeholder="Assigned to" value={filters.assigned_user_id} onChange={(v) => setFilters((f) => ({ ...f, assigned_user_id: v || null }))} options={[{ value: 'none', label: 'Unassigned' }, ...staffOptions]} />}
          <Select allowClear showSearch optionFilterProp="label" style={{ width: 200 }} placeholder="Client" value={filters.client_id} onChange={(v) => setFilters((f) => ({ ...f, client_id: v || null }))} options={clients.map((x) => ({ value: x.id, label: x.client_name }))} />
          <Input.Search placeholder="Search subject" allowClear style={{ width: 180 }} onSearch={(v) => setFilters((f) => ({ ...f, q: v }))} />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Link to="/tasks"><Button icon={<PlusOutlined />}>New task</Button></Link>
        </Space>
      </div>

      {c && (
        <Space size={16} style={{ marginBottom: 12 }} wrap>
          <Badge count={c.overdue} color="#cf1322" showZero overflowCount={999}><Tag style={{ padding: '2px 10px' }}>Overdue</Tag></Badge>
          <Badge count={c.open} color="#1677ff" showZero overflowCount={999}><Tag style={{ padding: '2px 10px' }}>To do</Tag></Badge>
          <Badge count={c.working} color="#d48806" showZero overflowCount={999}><Tag style={{ padding: '2px 10px' }}>In progress</Tag></Badge>
          <Badge count={c.completed_recent} color="#389e0d" showZero overflowCount={999}><Tag style={{ padding: '2px 10px' }}>Done recently</Tag></Badge>
          {isManager && <Badge count={c.unassigned} color="#8c8c8c" showZero overflowCount={999}><Tag style={{ padding: '2px 10px' }}>Unassigned (all)</Tag></Badge>}
        </Space>
      )}

      <Row gutter={16}>
        <Col xs={24} xl={17}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            {COLUMNS.map((col) => {
              const list = board ? board.tasks.filter((t) => t.status === col.key) : [];
              return (
                <div
                  key={col.key}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => onDrop(e, col.key)}
                  style={{ background: dragOver === col.key ? '#eef2ff' : '#f5f6fa', borderRadius: 8, padding: 10, minHeight: 320, border: `1px dashed ${dragOver === col.key ? '#5b6cff' : 'transparent'}` }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 600, color: col.color }}>
                    <span>{col.title}</span>
                    <span>{list.length}</span>
                  </div>
                  {list.map((t) => (
                    <TaskCard key={t.id} task={t} today={board.today} onOpen={openTask} />
                  ))}
                  {!list.length && <Typography.Text type="secondary" style={{ fontSize: 12 }}>Nothing here</Typography.Text>}
                </div>
              );
            })}
          </div>
        </Col>
        <Col xs={24} xl={7}>
          {board && isManager && (
            <Card size="small" title="Team workload" style={{ marginBottom: 12 }} bodyStyle={{ padding: 8 }}>
              {board.staff.map((s) => (
                <div
                  key={s.id}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(`u${s.id}`); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => onDropStaff(e, s.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: dragOver === `u${s.id}` ? '#eef2ff' : undefined, cursor: 'default' }}
                >
                  <Avatar size={26} style={{ background: '#5b6cff', fontSize: 11 }}>{initials(s.name)}</Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Typography.Link onClick={() => setFilters((f) => ({ ...f, mine: false, assigned_user_id: s.id }))}>{s.name}</Typography.Link>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      {s.open_tasks} to do, {s.working_tasks} in progress{s.overdue_tasks ? <span style={{ color: '#cf1322' }}>, {s.overdue_tasks} overdue</span> : ''}{s.due_this_week ? `, ${s.due_this_week} due this week` : ''}
                    </div>
                  </div>
                  <Tooltip title="Completed in the last 7 days"><Tag color="green" style={{ marginRight: 0 }}>{s.completed_this_week}</Tag></Tooltip>
                </div>
              ))}
              <div onDragOver={(e) => { e.preventDefault(); setDragOver('u0'); }} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDropStaff(e, null)} style={{ padding: '6px 8px', fontSize: 12, color: '#6b7280', borderRadius: 6, background: dragOver === 'u0' ? '#eef2ff' : undefined }}>
                Drop here to unassign
              </div>
            </Card>
          )}
          {board && (
            <Card size="small" title={`Deadlines in the next 30 days (${board.deadlines.length})`} style={{ marginBottom: 12 }} bodyStyle={{ padding: 0 }}>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={board.deadlines}
                showHeader={false}
                locale={{ emptyText: 'No deadlines due' }}
                columns={[
                  { dataIndex: 'due_date', width: 90, render: (v, d) => <span style={{ color: d.overdue ? '#cf1322' : undefined, fontWeight: d.overdue ? 600 : 400 }}>{moment(v).format('DD MMM')}</span> },
                  { key: 'd', render: (_, d) => <span>{d.deadline_type}{d.period ? ` ${d.period}` : ''}<br /><Typography.Text type="secondary" style={{ fontSize: 12 }}>{d.client_name ? <Link to={`/clients/${d.client_id}`}>{d.client_name}</Link> : 'The firm'}{d.entity_name ? ` - ${d.entity_name}` : ''}</Typography.Text></span> },
                ]}
              />
            </Card>
          )}
          {board && (
            <Card size="small" title={`Open projects (${board.projects.length})`} bodyStyle={{ padding: 0 }}>
              <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 8, size: 'small', hideOnSinglePage: true }}
                dataSource={board.projects}
                showHeader={false}
                locale={{ emptyText: 'No open projects' }}
                columns={[
                  { key: 'p', render: (_, p) => <span><Link to={`/projects`}>{p.name}</Link><br /><Typography.Text type="secondary" style={{ fontSize: 12 }}>{p.client_name || 'Internal'} - {p.completed}/{p.tasks} tasks{p.overdue ? <span style={{ color: '#cf1322' }}>, {p.overdue} overdue</span> : ''}</Typography.Text></span> },
                  { dataIndex: 'progress', width: 90, render: (v, p) => <Progress percent={v} size="small" status={p.overdue ? 'exception' : v === 100 ? 'success' : 'normal'} /> },
                ]}
              />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
